import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { addTrip } from '../../store/slices/tripSlice';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Alert,
  CircularProgress,
  InputAdornment,
  FormControl,
  MenuItem,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PhoneIcon from '@mui/icons-material/Phone';
import EditIcon from '@mui/icons-material/Edit';
import { api } from '../../services/api';
import TripSuccessDialog from './TripSuccessDialog';
import TripCreatedSummary from './TripCreatedSummary';
import {
  formContainerSx,
  formRowSx,
  formFieldSx,
  sectionTitleSx,
  customerDetailsContainerSx,
  customerHeaderSx,
  customerContentSx,
  customerInfoGridSx,
  customerInfoFieldSx,
  customerInfoLabelSx,
  customerInfoValueSx,
  customerEditFieldSx,
  customerEditFieldDisabledSx,
  customerEditSelectSx,
  customerEditAddressSx,
  tripCreatedSimpleBoxSx,
  tripCreatedSimpleCodeSx
} from '../layout/styles/NewTripForm.styles';
import { TripFormPersistenceService } from '../../services/tripFormPersistence';
import TripInformationSection from './TripInformationSection';
import CustomerInfoDisplay from './CustomerInfoDisplay';
import CustomerEditForm from './CustomerEditForm';
import {
  TripPricing, TripFormData,
  CustomerData,
  CustomerUpdateData,
  CustomerLookupResponse,
  TripCreationResponse,
  PricingTableData
} from '../../types/newTripForm';
import Select from '@mui/material/Select';

interface NewTripFormProps {
  onClose: () => void;
  onTripCreated?: (createdTripData?: any) => void;
}

const COUNTRY_CODES = [
  { code: '+91', label: 'India (+91)',maxLength: 10 },
]

const NewTripForm: React.FC<NewTripFormProps> = ({ onClose, onTripCreated }) => {
  const dispatch = useDispatch();
  // Customer lookup state
  const [countryCode, setCountryCode] = useState('+91');
  const [customerMobile, setCustomerMobile] = useState('');
  const [customer, setCustomer] = useState<CustomerData | null>(null);
  const [lookingUpCustomer, setLookingUpCustomer] = useState(false);
  const [customerLookupError, setCustomerLookupError] = useState('');
  const [customerLookupMessage, setCustomerLookupMessage] = useState('');
  const [showTripForm, setShowTripForm] = useState(false);
  const [isEditingCustomer, setIsEditingCustomer] = useState(false);
  const [customerEditData, setCustomerEditData] = useState<CustomerUpdateData>({
    name: '',
    email: '',
    gender: '',
    address: ''
  });
  const [updatingCustomer, setUpdatingCustomer] = useState(false);
  const [customerUpdateError, setCustomerUpdateError] = useState('');
  // Form state
  const [tripData, setTripData] = useState<TripFormData>({
    customerId: '',
    travelServiceDetailId: '',
    travelfleetDetailId: '',
    startDatetime: null,
    endDatetime: null,
    sourceCity: '',
    destinationCities: '',
    request: '',
    notes: '',
    driverId: '',
    carId: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [tripCreationResponse, setTripCreationResponse] = useState<TripCreationResponse | null>(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [pricingTable, setPricingTable] = useState<PricingTableData | null>(null);
  const [serviceOptions, setServiceOptions] = useState<any[]>([]);
  const [fleetOptions, setFleetOptions] = useState<any[]>([]);
  const [createdTripCode, setCreatedTripCode] = useState<string | null>(null);
  const [calculatedPrice, setCalculatedPrice] = useState<number | null>(null);
  const [calculatedDays, setCalculatedDays] = useState<number>(1);
  const [estimatedTripFare, setEstimatedTripFare] = useState<number>(0);

  // Add Mumbai Airport and Oneway specific state
  const [isMumbaiAirportService, setIsMumbaiAirportService] = useState(false);
  const [airportPickupDrop, setAirportPickupDrop] = useState<string>('');
  const [isOnewayService, setIsOnewayService] = useState(false);
  const [onewayRoute, setOnewayRoute] = useState<string>('');
  const [isPerPaxService, setIsPerPaxService] = useState(false);
  const [perPaxRoute, setPerPaxRoute] = useState<string>('');
  const [isOtherService, setIsOtherService] = useState(false);

   // Get max length for current country code
  const currentMaxLength = useMemo(() => {
    const country = COUNTRY_CODES.find(c => c.code === countryCode);
    return country?.maxLength || 10;
  }, [countryCode]);
  // Add helper function to check if service should hide end date
  const shouldHideEndDate = useMemo(() => {
    if (!tripData.travelServiceDetailId || !serviceOptions.length) return false;

    const selectedService = serviceOptions.find(s => s.id === Number(tripData.travelServiceDetailId));
    const serviceName = selectedService?.name?.toLowerCase() || '';

    // Hide end date for airport, oneway, per pax, and pune local services
    return isMumbaiAirportService ||
      isOnewayService ||
      isPerPaxService ||
      isOtherService ||
      serviceName.includes('pune local') ||
      serviceName.includes('airport') ||
      serviceName.includes('oneway') ||
      serviceName.includes('one way') ||
      serviceName.includes('per pax') ||
      serviceName.includes('others');
  }, [tripData.travelServiceDetailId, serviceOptions, isMumbaiAirportService, isOnewayService, isPerPaxService,isOtherService]);

  // Load persisted data on component mount
  useEffect(() => {
    const persistedData = TripFormPersistenceService.getFormData();
    if (persistedData) {

      setCustomerMobile(persistedData.customerMobile);
      setCustomer(persistedData.customer);
      setCustomerLookupMessage(persistedData.customerLookupMessage);
      setShowTripForm(persistedData.showTripForm);
      setIsEditingCustomer(persistedData.isEditingCustomer);
      setCustomerEditData(persistedData.customerEditData);

      // Restore Mumbai Airport state
      if (persistedData.isMumbaiAirportService) {
        setIsMumbaiAirportService(persistedData.isMumbaiAirportService);
      }
      if (persistedData.airportPickupDrop) {
        setAirportPickupDrop(persistedData.airportPickupDrop);
      }

      // Restore Oneway service state
      if (persistedData.isOnewayService) {
        setIsOnewayService(persistedData.isOnewayService);
      }
      if (persistedData.onewayRoute) {
        setOnewayRoute(persistedData.onewayRoute);
      }

      // Restore Per Pax service state
      if (persistedData.isPerPaxService) {
        setIsPerPaxService(persistedData.isPerPaxService);
      }
      if (persistedData.perPaxRoute) {
        setPerPaxRoute(persistedData.perPaxRoute);
      }

      // Restore Per Pax service state
      if (persistedData.isPerPaxService) {
        setIsPerPaxService(persistedData.isPerPaxService);
      }
      if (persistedData.perPaxRoute) {
        setPerPaxRoute(persistedData.perPaxRoute);
      }
      if (persistedData.isOtherService) {
        setIsOtherService(persistedData.isOtherService);
      }

      // Restore trip creation data if it exists
      if (persistedData.createdTripCode) {
        setCreatedTripCode(persistedData.createdTripCode);
      }
      if (persistedData.tripCreationResponse) {
        setTripCreationResponse(persistedData.tripCreationResponse);
      }

      // Ensure dates are properly converted to Date objects
      const restoredTripData = {
        ...persistedData.tripData,
        startDatetime: persistedData.tripData.startDatetime
          ? (persistedData.tripData.startDatetime instanceof Date
            ? persistedData.tripData.startDatetime
            : new Date(persistedData.tripData.startDatetime))
          : null,
        endDatetime: persistedData.tripData.endDatetime
          ? (persistedData.tripData.endDatetime instanceof Date
            ? persistedData.tripData.endDatetime
            : new Date(persistedData.tripData.endDatetime))
          : null
      };

      setTripData(restoredTripData);
      setErrors(persistedData.errors);
      setSubmitSuccess(persistedData.submitSuccess);
    }
  }, []);

  // Save form data whenever relevant state changes
  useEffect(() => {
    // Don't save if form is being submitted, but allow saving after successful creation
    if (isSubmitting) return;

    const dataToSave = {
      countryCode,
      customerMobile,
      customer,
      customerLookupMessage,
      showTripForm,
      isEditingCustomer,
      customerEditData,
      tripData,
      errors,
      submitSuccess,
      createdTripCode,
      tripCreationResponse,
      isMumbaiAirportService,
      airportPickupDrop,
      isOnewayService,
      onewayRoute,
      isPerPaxService,
      perPaxRoute,
      isOtherService,
      timestamp: new Date().toISOString()
    };

    // Always save if there's any meaningful data - remove restrictive conditions
    const hasData = customerMobile ||
      customer ||
      showTripForm ||
      createdTripCode ||
      submitSuccess ||
      isMumbaiAirportService ||
      isOnewayService ||
      isPerPaxService ||
      isOtherService ||
      isEditingCustomer ||
      Object.values(tripData).some(value => {
        if (value === null || value === undefined || value === '') return false;
        if (typeof value === 'string' && value.trim() === '') return false;
        if (typeof value === 'number' && value === 0) return false;
        return true;
      }) ||
      Object.keys(errors).length > 0;

    if (hasData) {
      TripFormPersistenceService.saveFormData(dataToSave);
    } else {

    }
  }, [
    customerMobile,
    customer,
    customerLookupMessage,
    showTripForm,
    isEditingCustomer,
    customerEditData,
    tripData,
    errors,
    submitSuccess,
    isSubmitting,
    createdTripCode,
    tripCreationResponse,
    isMumbaiAirportService,
    airportPickupDrop,
    isOnewayService,
    onewayRoute,
    isPerPaxService,
    perPaxRoute,
    isOtherService
  ]);

  const fleetDetailsInfo = useMemo(() => {
    if (!pricingTable?.data || !tripData?.travelServiceDetailId || !tripData?.travelfleetDetailId) {
      return null;
    }

    const serviceIndex = Number(tripData.travelServiceDetailId) - 1;
    const fleetIndex = Number(tripData.travelfleetDetailId) - 1;

    if (serviceIndex < 0 || fleetIndex < 0) {
      return null;
    }

    const service = pricingTable.data[serviceIndex];
    if (!service?.carOptions) {
      return null;
    }

    return service.carOptions[fleetIndex] || null;
  }, [pricingTable, tripData.travelServiceDetailId, tripData.travelfleetDetailId]);

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const data = await api.getPricingTable() as PricingTableData;
        setPricingTable(data);

        if (data?.data && Array.isArray(data.data)) {
          // Create service options with proper ID mapping
          const services = data.data.map((service: any, serviceIndex: number) => {
            return {
              id: serviceIndex + 1, // Display ID
              serviceIndex: serviceIndex, // Keep track of array index
              name: service.serviceName || `Service ${serviceIndex + 1}`,
              carOptions: service.carOptions || []
            };
          });
          setServiceOptions(services);
        }
      } catch (err: any) {
        console.error('Failed to fetch pricing table:', err.message || err);
      }
    };

    fetchPricing();
  }, []);

  // Update fleet options when service is selected
  useEffect(() => {
    if (tripData.travelServiceDetailId && pricingTable?.data) {
      const serviceIndex = Number(tripData.travelServiceDetailId) - 1;
      const selectedService = pricingTable.data[serviceIndex];

      if (selectedService?.carOptions && Array.isArray(selectedService.carOptions)) {
        const fleetOpts = selectedService.carOptions.map((fleet: any, fleetIndex: number) => {
          const carType = fleet.carType || 'Vehicle';
          const makeModel = fleet.makeModel || fleet.makeAndModel || fleet.model || fleet.name || 'Model';
          const capacity = fleet.capacity || 'N/A';

          return {
            id: fleetIndex + 1, // Display ID
            fleetIndex: fleetIndex, // Keep track of array index
            actualDatabaseId: fleet.id || fleet.travelServiceDetailId, // Use the actual database ID from API
            displayName: fleet.slot && fleet.slot !== ''
              ? `${carType} | ${makeModel} | ${capacity} (${fleet.slot})`
              : `${carType} | ${makeModel} | ${capacity}`,
            fleetDetails: fleet,
            slot: fleet.slot
          };
        });
        setFleetOptions(fleetOpts);
      } else {
        setFleetOptions([]);
      }
    } else {
      setFleetOptions([]);
    }
  }, [tripData.travelServiceDetailId, pricingTable]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // For OTHER service type, only validate customer and basic info
    if (isOtherService) {
      // No validation needed for OTHER service type
      // Customer ID is already validated by customer lookup
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    }

    // Validate required fields for normal services
    if (!tripData.travelServiceDetailId) {
      newErrors.service = 'Service selection is required';
    }
    if (!tripData.travelfleetDetailId) {
      newErrors.fleet = 'Vehicle selection is required';
    }

    // For Mumbai Airport service, validate pickup/drop selection
    if (isMumbaiAirportService && !airportPickupDrop) {
      newErrors.airportPickupDrop = 'Please select Pickup or Drop';
    }

    // For Oneway service, validate route selection
    if (isOnewayService && !onewayRoute) {
      newErrors.onewayRoute = 'Please select route direction';
    }

    // For Per Pax service, validate route selection
    if (isPerPaxService && !perPaxRoute) {
      newErrors.perPaxRoute = 'Please select route direction';
    }

    if (!tripData.sourceCity) newErrors.sourceCity = 'Source city is required';
    if (!tripData.destinationCities) newErrors.destinationCities = 'Destination is required';
    if (!tripData.startDatetime) newErrors.startDatetime = 'Start date & time is required';
    if(!tripData.request) newErrors.request = 'Lead Source is required';

    if (tripData.travelServiceDetailId && serviceOptions.length > 0) {
    const selectedService = serviceOptions.find(s => s.id === Number(tripData.travelServiceDetailId));
    const serviceName = selectedService?.name?.toLowerCase() || '';
    const isOutstationService = serviceName.includes('outstation');

  if (isOutstationService) {
      // Check if end datetime is provided
      if (!tripData.endDatetime) {
        newErrors.endDatetime = 'End date & time is required for outstation service';
      } else if (tripData.startDatetime && tripData.endDatetime) {
        const startDate = new Date(tripData.startDatetime);
        const endDate = new Date(tripData.endDatetime);
        
        // Check if end date is before start date
        if (endDate < startDate) {
          newErrors.endDatetime = 'End date & time cannot be before start date & time';
        } else {
          // Validate that end datetime is at least 6 hours after start datetime
          const diffInMilliseconds = endDate.getTime() - startDate.getTime();
          const diffInHours = diffInMilliseconds / (1000 * 60 * 60);
          
          if (diffInHours < 6) {
            newErrors.endDatetime = 'End date & time must be at least 6 hours after start date & time';
          }
        }
      }
    }
  }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrors({}); // Clear previous errors

    try {
      // Special handling for OTHER service type
      if (isOtherService) {

        // For OTHER service, find the selected fleet's actual database ID
        const selectedFleet = fleetOptions.find(f => f.id === Number(tripData.travelfleetDetailId));
        const actualTravelServiceDetailId = selectedFleet?.actualDatabaseId;

        if (!actualTravelServiceDetailId) {
          throw new Error('Please select a vehicle for OTHER service type.');
        }
        const otherServiceData = {
          customerId: parseInt(tripData.customerId),
          travelServiceDetailId: actualTravelServiceDetailId,
          // travelServiceDetailId: ,
          sourceCity: 'N/A', // Send placeholder text
          destinationCities: 'N/A', // Send placeholder text
          startDatetime: new Date().toISOString().split('.')[0], // Send current date
          endDatetime: new Date().toISOString().split('.')[0], // Send current date
          request: tripData.request || '',
          notes: tripData.notes || '',
          // serviceType: 'OTHER' // Add a flag to indicate this is OTHER service type
        };

        const response = await api.createTrip(otherServiceData as any) as TripCreationResponse;

        if (response && response.status === true && response.data) {
          setTripCreationResponse(response);
          setCreatedTripCode(response.data.tripCode);
          setShowSuccessDialog(true);
          setSubmitSuccess(true);

          // Create trip object for Redux store
          const newTrip = {
            tripId: response.data.tripCode,
            trip_id: response.data.tripCode,
            tripType: 'OTHER',
            carType: '',
            carModel: '',
            ...response.data
          };
          
          dispatch(addTrip(newTrip));
          
          if (onTripCreated) {
            onTripCreated({
              tripId: response.data.tripCode,
              ...response.data
            });
          }
        } else {
          throw new Error(response?.message || 'Failed to create trip');
        }

        setIsSubmitting(false);
        return;
      }

      // Normal trip creation flow for other service types
      // Helper function to safely convert date to local ISO string without timezone conversion
      const dateToISOString = (date: Date | string | null): string => {
        if (!date) return '';
        if (typeof date === 'string') return date;
        if (date instanceof Date) {
          // Use local time components to avoid timezone conversion
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const hours = String(date.getHours()).padStart(2, '0');
          const minutes = String(date.getMinutes()).padStart(2, '0');
          const seconds = String(date.getSeconds()).padStart(2, '0');
          return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
        }
        return '';
      };

      // Find the actual database ID from the selected fleet option
      const selectedFleet = fleetOptions.find(f => f.id === Number(tripData.travelfleetDetailId));
      const actualTravelServiceDetailId = selectedFleet?.actualDatabaseId;

      if (!actualTravelServiceDetailId) {
        throw new Error('Invalid fleet selection. Please select a valid vehicle.');
      }

      // Get pricing details from selected fleet
      const fleetDetails = selectedFleet?.fleetDetails;
      const selectedService = serviceOptions.find(s => s.id === Number(tripData.travelServiceDetailId));
      const serviceName = selectedService?.name?.toLowerCase() || '';

      // Calculate agreed trip fare and discount based on service type
      let estimatedFare = estimatedTripFare || 0
      let agreedTripFare = tripData.agreedPrice !== null && tripData.agreedPrice !== undefined 
        ? tripData.agreedPrice 
        : (calculatedPrice || fleetDetails?.offerPrice || fleetDetails?.displayPrice || 0);
      
      let discount = 0;
      
      // Calculate discount for specific service types
      if (isMumbaiAirportService || isOnewayService || isPerPaxService ||
          serviceName.includes('airport') || 
          serviceName.includes('oneway') || 
          serviceName.includes('round trip') ||
          serviceName.includes('one way') || 
          serviceName.includes('pune local') ||
          serviceName.includes('per pax')) {
        
        // For these services: discount = estimatedPrice - agreedPrice
        discount = estimatedFare - agreedTripFare;
        
        // Ensure discount is not negative
        if (discount < 0) {
          discount = 0;
        }
      }
      const pricing: TripPricing = {
        ratePerDay: fleetDetails?.offerPrice || 0,
        noOfDays: calculatedDays,
        ratePerExtraKm: fleetDetails?.ratePerExtraKm || 0,
        ratePerExtraHour: fleetDetails?.ratePerExtraHour || 0, // Default value
        driverAllowancePerDay: fleetDetails?.driverAllowance, // Default value
        estimatedTripFare: estimatedFare || 0,
        discount: discount || 0,
        agreedTripFare: tripData.agreedPrice !== null && tripData.agreedPrice !== undefined 
          ? tripData.agreedPrice 
          : (calculatedPrice || fleetDetails?.offerPrice || fleetDetails?.displayPrice || 0),
        description: `${tripData.sourceCity} to ${tripData.destinationCities} trip`
      };

      const formattedData = {
        customerId: parseInt(tripData.customerId),
        travelServiceDetailId: actualTravelServiceDetailId,
        sourceCity: tripData.sourceCity,
        destinationCities: tripData.destinationCities,
        startDatetime: dateToISOString(tripData.startDatetime),
        endDatetime: dateToISOString(tripData.endDatetime),
        request: tripData.request,
        notes: tripData.notes,
        driverId: tripData.driverId ? Number(tripData.driverId) : undefined,
        carId: tripData.carId ? Number(tripData.carId) : undefined,
        pricing: pricing,// Add pricing information
        vendorGst:tripData.vendorGst
      };


      // Use the correct endpoint and type the response
      const response = await api.createTrip(formattedData) as TripCreationResponse;

      if (response && response.status === true && response.data) {
        setTripCreationResponse(response);
        setCreatedTripCode(response.data.tripCode); // Store the trip code
        setShowSuccessDialog(true);
        setSubmitSuccess(true);

        // Automatically add a passenger with the customer's information
        if (customer) {
          try {
            // Create passenger data object using customer information
            const passengerData = {
              passengerName: customer.name || '',
              contactNumber: customer.mobileNumber || '',
              email: customer.email || '',
              pickupInfo: '',
              dropInfo: '',
              specialRequirements: '',
              tripForSelf: true // Set as trip for self
            };

            // Add passenger to the trip without showing UI
            await api.addTripPassenger(response.data.tripCode, passengerData);

          } catch (passengerError) {
            console.error('Error adding passenger automatically:', passengerError);
            // Don't show error to user as this is a background operation
          }
        }

        // Create trip object for Redux store
        const newTrip = {
          tripId: response.data.tripCode,
          trip_id: response.data.tripCode,
          tripType: 'TRIP_DETAILS',
          carType: '',
          carModel: '',
          // Don't include operator here as it will be overwritten by spread
          ...response.data
        };
        
        // Dispatch to Redux store
        dispatch(addTrip(newTrip));
        
        // Call the callback to refresh trip list and update tab
        if (onTripCreated) {
          onTripCreated({
            tripId: response.data.tripCode,
            ...response.data
          });
        }
      } else {
        throw new Error(response?.message || 'Failed to create trip');
      }
    } catch (error: any) {
      console.error('Error creating trip:', error);

      // Handle different error types
      let errorMessage = 'Failed to create trip. Please try again.';

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setErrors({
        submit: errorMessage
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSuccessDialogClose = () => {
    TripFormPersistenceService.clearFormData();
    onClose();
    setShowSuccessDialog(false);
  };

  const handleSuccessDialogContinue = () => {
    setShowSuccessDialog(false);
  };

  const handleSuccessDialogCreateAnother = () => {
    setShowSuccessDialog(false);
    handleStartOver();
  };

  const handleSuccessDialogNavigateToTrip = (tripCode: string) => {
    setShowSuccessDialog(false);

    // Mark navigation as complete in persistence
    TripFormPersistenceService.markNavigationComplete();
    onClose();
    // Small delay to ensure the close happens first, then navigate
    setTimeout(() => {
      if (onTripCreated) {
        onTripCreated({
          tripCode: tripCode,
          tripId: tripCode,
          directNavigation: true, // New flag for direct navigation
          navigateToDetails: true,
          updateTabName: true
        });
      }
    }, 100);
  };

  // Update cleanup effect to be more specific about when to clear data
  useEffect(() => {
    return () => {
      // Only clear data in very specific cases to preserve tab switching
      const shouldClear = !submitSuccess &&
        !createdTripCode &&
        !customer &&
        !showTripForm &&
        !customerMobile &&
        !isMumbaiAirportService &&
        !isOnewayService &&
        !isPerPaxService &&
        !isOtherService;

      if (shouldClear) {
        TripFormPersistenceService.clearFormData();
      } else {
      }
    };
  }, [submitSuccess, createdTripCode, customer, showTripForm, customerMobile, isMumbaiAirportService, isOnewayService]);

  const handleCustomerLookup = async () => {
    if (!customerMobile || customerMobile.length !== 10) {
      setCustomerLookupError('Please enter a valid 10-digit mobile number');
      return;
    }

    setLookingUpCustomer(true);
    setCustomerLookupError('');
    setCustomerLookupMessage('');

    try {

      const fullMobileNumber = `${countryCode.replace('+', '')}${customerMobile}`;
      const response = await api.lookupOrCreateCustomer(fullMobileNumber, countryCode.replace("+", "")) as CustomerLookupResponse;

      if (response.status && response.data) {
        setCustomer(response.data);
        setCustomerLookupMessage(response.message); // Store the API message
        setTripData(prev => ({
          ...prev,
          customerId: response.data.id.toString(),
          contactNumber: response.data.mobileNumber,
          email: response.data.email || '',
          passengerName: response.data.name || ''
        }));
        setShowTripForm(true);

        // If the message contains "New customer created", automatically enter edit mode
        if (response.message && response.message.toLowerCase().includes('new customer created')) {
          // Prepare edit data with existing values
          setCustomerEditData({
            name: response.data.name || '',
            email: response.data.email || '',
            gender: response.data.gender || '',
            address: response.data.address || ''
          });
          // Enter edit mode
          setIsEditingCustomer(true);
        }
      } else {
        throw new Error(response.message || 'Failed to lookup customer');
      }
    } catch (error: any) {
      console.error('Error looking up customer:', error);

      // Map different error scenarios
      let errorMessage = 'Failed to lookup customer. Please try again.';

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setCustomerLookupError(errorMessage);
    } finally {
      setLookingUpCustomer(false);
    }
  };

  const handleMobileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 10) {
      setCustomerMobile(value);
      setCustomerLookupError('');
    }
  };

   const handleCountryCodeChange = (e: any) => {
    setCountryCode(e.target.value);
    setCustomerMobile(''); // Clear mobile number when country changes
    setCustomerLookupError('');
  };

  const handleStartOver = () => {
    setCustomer(null);
    setCustomerMobile('');
    setShowTripForm(false);
    setCustomerLookupError('');
    setSubmitSuccess(false);
    setCreatedTripCode(null);
    setTripCreationResponse(null);
    setIsMumbaiAirportService(false);
    setAirportPickupDrop('');
    setIsOnewayService(false);
    setOnewayRoute('');
    setIsPerPaxService(false);
    setPerPaxRoute('');
    setIsOtherService(false);
    setCountryCode('+91');
    setTripData({
      customerId: '',
      travelServiceDetailId: '',
      travelfleetDetailId: '',
      startDatetime: null,
      endDatetime: null,
      sourceCity: '',
      destinationCities: '',
      request: '',
      notes: '',
      driverId: '',
      carId: ''
    });
    setErrors({});

    // Clear persisted data when explicitly starting over
    TripFormPersistenceService.clearFormData();
  };

  const handleCustomerEdit = () => {
    if (customer) {
      setCustomerEditData({
        name: customer.name || '',
        email: customer.email || '',
        gender: customer.gender || '',
        address: customer.address || ''
      });
      setIsEditingCustomer(true);
      setCustomerUpdateError('');
    }
  };

  const handleCustomerEditCancel = () => {
    setIsEditingCustomer(false);
    setCustomerUpdateError('');
  };

  const handleCustomerUpdate = async () => {
    if (!customer) return;

    setUpdatingCustomer(true);
    setCustomerUpdateError('');

    try {
      const response = await api.updateCustomer(customer.id, customerEditData) as { status: boolean; message: string; data: CustomerData };

      if (response.status && response.data) {
        // Update the customer state with new data
        setCustomer(response.data);

        // Update trip form data with new customer info
        setTripData(prev => ({
          ...prev,
          contactNumber: response.data.mobileNumber,
          email: response.data.email || '',
          passengerName: response.data.name || ''
        }));

        setIsEditingCustomer(false);
      } else {
        throw new Error(response.message || 'Failed to update customer');
      }
    } catch (error: any) {
      console.error('Error updating customer:', error);
      setCustomerUpdateError(error.message || 'Failed to update customer. Please try again.');
    } finally {
      setUpdatingCustomer(false);
    }
  };

  const handleCustomerEditChange = (field: keyof CustomerUpdateData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomerEditData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
  };

  const handleCalculatedDaysChange = (days: number) => {
    setCalculatedDays(days);
  }
  const handleEstimatedPriceChange = (price: number) => {
    setEstimatedTripFare(price);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ mb: 4, height: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column' }}>
        {/* <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5" color="primary">
            {createdTripCode ? `Trip Created: ${createdTripCode}` : 'New Trip'}
          </Typography>
          <IconButton onClick={onClose} color="primary">
            <CloseIcon />
          </IconButton>
        </Box> */}

        {/* Show success message */}
        {/* {submitSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Trip created successfully! Code: {createdTripCode}
          </Alert>
        )} */}

        {/* Show error message */}
        {errors.submit && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errors.submit}
          </Alert>
        )}

        {/* Scrollable content area */}
        <Box sx={{ flex: 1, overflowY: 'auto', pr: 2, pb: 6 }}>
          {/* Show trip creation details if trip was created successfully */}
          {createdTripCode && tripCreationResponse && (
            <TripCreatedSummary
              createdTripCode={createdTripCode}
              tripCreationResponse={tripCreationResponse}
              onStartOver={handleStartOver}
              onClose={() => {
                TripFormPersistenceService.clearFormData();
                onClose();
              }}
            />
          )}

          {/* Customer Lookup Section - Only show if no trip created yet */}
          {!showTripForm && !createdTripCode && (
            <Paper sx={formContainerSx}>
              <Typography variant="h6" sx={sectionTitleSx}>Customer Information</Typography>
              <Typography variant="body2" sx={{ mb: 3, color: 'rgba(255,255,255,0.7)' }}>
                Enter customer mobile number to lookup existing customer or create new one
              </Typography>
              <Box sx={formRowSx}>
                <Box sx={formFieldSx}>
                {/* Country Code Dropdown */}
                <Box sx={{ display: 'flex', gap: 2, flex: 1 }}>
                  <FormControl sx={{ minWidth: 130 }}>
                    <Select
                      value={countryCode}
                      onChange={handleCountryCodeChange}
                      disabled={lookingUpCustomer}
                      sx={{
                        height: 56,
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        '& .MuiSelect-select': {
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1
                        }
                      }}
                    >
                      {COUNTRY_CODES.map((country) => (
                        <MenuItem key={`${country.code}`} value={country.code}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {/* <span style={{ fontSize: '1.2rem' }}>{country.flag}</span> */}
                            <span>{country.label}</span>
                            {/* <span style={{ fontSize: '0.85rem', opacity: 0.7 }}>({country.country})</span> */}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    fullWidth
                    label="Customer Mobile Number"
                    value={customerMobile}
                    onChange={handleMobileChange}
                    placeholder="Enter 10-digit mobile number"
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                    error={!!customerLookupError}
                    helperText={customerLookupError}
                    disabled={lookingUpCustomer}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>  
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleCustomerLookup}
                    disabled={lookingUpCustomer || customerMobile.length !== 10}
                    startIcon={lookingUpCustomer ? <CircularProgress size={20} /> : <SearchIcon />}
                    sx={{ height: 56 }}
                  >
                    {lookingUpCustomer ? 'Looking up...' : 'Lookup Customer'}
                  </Button>
                </Box>
              </Box>
            </Paper>
          )}

          {/* Customer Found/Created Info - Only show if no trip created yet */}
          {customer && showTripForm && !createdTripCode && (
            <Paper sx={customerDetailsContainerSx}>
              <Box sx={customerHeaderSx}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6" fontWeight="bold">
                    CUSTOMER INFORMATION
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {!isEditingCustomer ? (
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{
                        color: 'white',
                        borderColor: 'white',
                        '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
                      }}
                      onClick={handleCustomerEdit}
                      startIcon={<EditIcon />}
                    >
                      MODIFY
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleCustomerUpdate}
                        disabled={updatingCustomer}
                        sx={{
                          color: 'white',
                          borderColor: 'white',
                          '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
                        }}
                      >
                        {updatingCustomer ? 'SAVING...' : 'SAVE'}
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleCustomerEditCancel}
                        disabled={updatingCustomer}
                        sx={{
                          color: 'white',
                          borderColor: 'white',
                          '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
                        }}
                      >
                        CANCEL
                      </Button>
                    </>
                  )}
                </Box>
              </Box>
              {customerLookupMessage && (
                <Box sx={{ mb: 2 }}>
                  <Alert
                    severity={customer.isExisting ? "info" : "success"}
                    sx={{
                      backgroundColor: customer.isExisting ? 'rgba(33, 150, 243, 0.1)' : 'rgba(76, 175, 80, 0.1)',
                      border: customer.isExisting ? '1px solid rgba(33, 150, 243, 0.3)' : '1px solid rgba(76, 175, 80, 0.3)'
                    }}
                  >
                    {customerLookupMessage}
                  </Alert>
                </Box>
              )}
              <Box sx={customerContentSx}>
                {!isEditingCustomer ? (
                  <CustomerInfoDisplay
                    customer={customer}
                    customerInfoGridSx={customerInfoGridSx}
                    customerInfoFieldSx={customerInfoFieldSx}
                    customerInfoLabelSx={customerInfoLabelSx}
                    customerInfoValueSx={customerInfoValueSx}
                  />
                ) : (
                  <CustomerEditForm
                    customerEditData={customerEditData}
                    customer={customer}
                    customerUpdateError={customerUpdateError}
                    handleCustomerEditChange={handleCustomerEditChange}
                    setCustomerEditData={setCustomerEditData}
                    customerEditFieldSx={customerEditFieldSx}
                    customerEditFieldDisabledSx={customerEditFieldDisabledSx}
                    customerEditSelectSx={customerEditSelectSx}
                    customerEditAddressSx={customerEditAddressSx}
                  />
                )}

              </Box>
            </Paper>
          )}

          {/* Trip Form - Only show after customer lookup and before trip creation */}
          {showTripForm && !createdTripCode && (
            <TripInformationSection
              tripData={tripData}
              onTripDataChange={setTripData}
              errors={errors}
              onErrorsChange={setErrors}
              serviceOptions={serviceOptions}
              fleetOptions={fleetOptions}
              pricingTable={pricingTable || { data: [] }} // Provide default value when null
              isSubmitting={isSubmitting}
              onSubmit={handleSubmit}
              onClose={onClose}
              isMumbaiAirportService={isMumbaiAirportService}
              isOnewayService={isOnewayService}
              isPerPaxService={isPerPaxService}
              isOtherService={isOtherService}
              airportPickupDrop={airportPickupDrop}
              onewayRoute={onewayRoute}
              perPaxRoute={perPaxRoute}
              onMumbaiAirportServiceChange={setIsMumbaiAirportService}
              onOnewayServiceChange={setIsOnewayService}
              onPerPaxServiceChange={setIsPerPaxService}
              onOtherServiceChange={setIsOtherService}
              onAirportPickupDropChange={setAirportPickupDrop}
              onOnewayRouteChange={setOnewayRoute}
              onPerPaxRouteChange={setPerPaxRoute}
              onCalculatedPriceChange={setCalculatedPrice}
              onCalculatedDaysChange={handleCalculatedDaysChange}
              onEstimatedPriceChange={handleEstimatedPriceChange}
            />
          )}

          {/* Show simple success message after trip creation - only if no detailed response */}
          {createdTripCode && !showSuccessDialog && !tripCreationResponse && (
            <Box sx={tripCreatedSimpleBoxSx}>
              <CheckCircleIcon sx={{ fontSize: 64, color: '#4caf50', mb: 2 }} />
              <Typography variant="h5" color="#4caf50" gutterBottom>
                Trip Created Successfully!
              </Typography>
              <Typography variant="h6" sx={tripCreatedSimpleCodeSx}>
                Trip Code: {createdTripCode}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  TripFormPersistenceService.clearFormData();
                  onClose();
                }}
                size="large"
              >
                Close & Continue
              </Button>
            </Box>
          )}
        </Box>
        {/* Success Dialog */}
        <TripSuccessDialog
          open={showSuccessDialog}
          onClose={handleSuccessDialogClose}
          onContinue={handleSuccessDialogContinue}
          onCreateAnother={handleSuccessDialogCreateAnother}
          onNavigateToTrip={handleSuccessDialogNavigateToTrip}
          tripCreationResponse={tripCreationResponse}
          isOtherService={isOtherService}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default NewTripForm;